import React, { useRef } from 'react';
import { ImagePlus, Grid2x2, Columns, Rows, LayoutGrid } from 'lucide-react';
import { Button } from '../ui/button';

const LAYOUTS = [
  {
    id: '2-side',
    name: '2-Up Side',
    icon: Columns,
    slots: [
      { x: 0, y: 0, w: 0.5, h: 1 },
      { x: 0.5, y: 0, w: 0.5, h: 1 },
    ]
  },
  {
    id: '2-stack',
    name: '2-Up Stack',
    icon: Rows,
    slots: [
      { x: 0, y: 0, w: 1, h: 0.5 },
      { x: 0, y: 0.5, w: 1, h: 0.5 },
    ]
  },
  {
    id: '3-left',
    name: '1 + 2',
    icon: LayoutGrid,
    slots: [
      { x: 0, y: 0, w: 0.5, h: 1 },
      { x: 0.5, y: 0, w: 0.5, h: 0.5 },
      { x: 0.5, y: 0.5, w: 0.5, h: 0.5 },
    ]
  },
  {
    id: '3-top',
    name: '1 + 2 Stack',
    icon: LayoutGrid,
    slots: [
      { x: 0, y: 0, w: 1, h: 0.5 },
      { x: 0, y: 0.5, w: 0.5, h: 0.5 },
      { x: 0.5, y: 0.5, w: 0.5, h: 0.5 },
    ]
  },
  {
    id: '4-grid',
    name: '4 Grid',
    icon: Grid2x2,
    slots: [
      { x: 0, y: 0, w: 0.5, h: 0.5 },
      { x: 0.5, y: 0, w: 0.5, h: 0.5 },
      { x: 0, y: 0.5, w: 0.5, h: 0.5 },
      { x: 0.5, y: 0.5, w: 0.5, h: 0.5 },
    ]
  },
];

const GAP = 8;

export function CollageBuilder({ layout, images, onSelectLayout, onAddImage, onRemoveImage, canvasSize = 1080, onOpenLibrary }) {
  const fileInputRef = useRef(null);
  const targetSlotRef = useRef(null);

  const currentLayout = LAYOUTS.find(l => l.id === layout) || LAYOUTS[0];

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    onAddImage(targetSlotRef.current, url, file);
    e.target.value = '';
  };

  const triggerFileInput = (slotIndex) => {
    targetSlotRef.current = slotIndex;
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-5" data-testid="collage-builder-controls">
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />

      {/* Layout Selection */}
      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-[#64748b] mb-2 block">Layout</label>
        <div className="flex gap-2">
          {LAYOUTS.map((l) => {
            const Icon = l.icon;
            return (
              <button
                key={l.id}
                onClick={() => onSelectLayout(l.id)}
                className={`flex-1 p-3 rounded-xl border transition-colors flex flex-col items-center gap-1 ${
                  layout === l.id ? 'border-[#2563eb] bg-[#eff6ff]' : 'border-[#e2e8f0] hover:border-[#2563eb]/50'
                }`}
                data-testid={`layout-${l.id}`}
              >
                <Icon className={`w-5 h-5 ${layout === l.id ? 'text-[#2563eb]' : 'text-[#94a3b8]'}`} />
                <span className="text-[9px] font-medium text-[#64748b]">{l.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Image Slots */}
      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-[#64748b] mb-2 block">
          Images ({Object.keys(images).length}/{currentLayout.slots.length})
        </label>
        <div className="space-y-2">
          {currentLayout.slots.map((slot, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg border border-[#e2e8f0] overflow-hidden flex-shrink-0 bg-[#f8fafc]">
                {images[i] ? (
                  <img src={images[i].url} alt={`Slot ${i + 1}`} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#cbd5e1]">
                    <ImagePlus className="w-5 h-5" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <span className="text-xs font-medium text-[#475569]">Slot {i + 1}</span>
                <span className="text-[10px] text-[#94a3b8] ml-2">
                  {Math.round(slot.w * 100)}% x {Math.round(slot.h * 100)}%
                </span>
              </div>
              {images[i] ? (
                <Button variant="ghost" size="sm" onClick={() => onRemoveImage(i)} className="text-red-400 hover:text-red-600 h-7 px-2 text-xs">
                  Remove
                </Button>
              ) : (
                <div className="flex gap-1">
                  {onOpenLibrary && (
                    <Button variant="outline" size="sm" onClick={() => onOpenLibrary(i)} className="border-[#2563eb]/30 text-[#2563eb] h-7 px-2 text-xs" data-testid={`library-slot-${i}`}>
                      Library
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => triggerFileInput(i)} className="border-[#e2e8f0] h-7 px-2 text-xs" data-testid={`add-image-slot-${i}`}>
                    <ImagePlus className="w-3 h-3 mr-1" /> File
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Background Color */}
      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-[#64748b] mb-2 block">Gap Color</label>
        <div className="flex gap-1.5">
          {['#ffffff', '#000000', '#1e3a5f', '#f1f5f9', '#c5d93d'].map(color => (
            <button
              key={color}
              className="w-7 h-7 rounded-lg border-2 border-[#e2e8f0]"
              style={{ backgroundColor: color }}
              data-testid={`gap-color-${color.replace('#', '')}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export { LAYOUTS, GAP };
